import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const colorMap = {
  blue: 'bg-blue-100 border-blue-300',
  green: 'bg-green-100 border-green-300',
  yellow: 'bg-yellow-100 border-yellow-300',
  red: 'bg-red-100 border-red-300',
  purple: 'bg-purple-100 border-purple-300',
};

const colorBgMap = {
  blue: 'rgba(191,219,254,0.6)',
  green: 'rgba(187,247,208,0.6)',
  yellow: 'rgba(254,240,138,0.6)',
  red: 'rgba(254,202,202,0.6)',
  purple: 'rgba(221,214,254,0.6)',
};

function colLabel(index) {
  let label = '';
  let n = index;
  while (n >= 0) {
    label = String.fromCharCode(65 + (n % 26)) + label;
    n = Math.floor(n / 26) - 1;
  }
  return label;
}

export default function SpreadsheetGrid({
  data = [],
  highlightCells = [],
  selectedCell = null,
  onCellClick,
  formulaBar,
  compact = false,
  className = '',
}) {
  if (!data.length) return null;

  const numCols = Math.max(...data.map((row) => row.length));
  const cellPad = compact ? 'px-2 py-0.5 text-xs' : 'px-3 py-1.5 text-sm';
  const cellWidth = compact ? 'min-w-[60px]' : 'min-w-[90px]';

  function getHighlight(row, col) {
    return highlightCells.find((h) => h.row === row && h.col === col);
  }

  function isSelected(row, col) {
    return selectedCell && selectedCell.row === row && selectedCell.col === col;
  }

  const selectedValue =
    selectedCell && data[selectedCell.row]
      ? data[selectedCell.row][selectedCell.col] ?? ''
      : '';

  return (
    <div className={`rounded-lg border border-gray-300 bg-white overflow-hidden shadow-sm ${className}`}>
      {/* Formula bar */}
      {formulaBar !== undefined && (
        <div className="flex items-center border-b border-gray-200 bg-gray-50 px-3 py-1.5 gap-2">
          <span className="text-xs font-semibold text-gray-500 shrink-0">
            {selectedCell ? `${colLabel(selectedCell.col)}${selectedCell.row + 1}` : ''}
          </span>
          <div className="w-px h-4 bg-gray-300" />
          <span className="text-sm font-mono text-gray-800 truncate">
            {formulaBar ?? selectedValue}
          </span>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="border-collapse w-full">
          {/* Column headers */}
          <thead>
            <tr>
              <th className="sticky left-0 z-10 bg-gray-100 border-b border-r border-gray-300 w-10 text-center text-xs text-gray-500 font-medium py-1" />
              {Array.from({ length: numCols }, (_, i) => (
                <th
                  key={i}
                  className={`bg-gray-100 border-b border-r border-gray-300 text-center text-xs text-gray-500 font-medium py-1 ${cellWidth}`}
                >
                  {colLabel(i)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((row, rowIdx) => (
              <tr key={rowIdx}>
                {/* Row number */}
                <td className="sticky left-0 z-10 bg-gray-100 border-b border-r border-gray-300 text-center text-xs text-gray-500 font-medium w-10 py-1 select-none">
                  {rowIdx + 1}
                </td>
                {Array.from({ length: numCols }, (_, colIdx) => {
                  const highlight = getHighlight(rowIdx, colIdx);
                  const selected = isSelected(rowIdx, colIdx);
                  const value = row[colIdx] ?? '';
                  const isHeader = rowIdx === 0;

                  return (
                    <td
                      key={colIdx}
                      className={`relative border-b border-r border-gray-200 ${cellPad} ${cellWidth} cursor-pointer select-none transition-colors duration-150 ${
                        isHeader ? 'font-semibold text-gray-700 bg-gray-50' : 'text-gray-800'
                      } ${selected ? 'z-20' : ''}`}
                      onClick={() => onCellClick?.(rowIdx, colIdx)}
                    >
                      <AnimatePresence>
                        {highlight && (
                          <motion.div
                            key={`highlight-${rowIdx}-${colIdx}-${highlight.color}`}
                            className="absolute inset-0 pointer-events-none"
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            transition={{ duration: 0.2 }}
                            style={{
                              backgroundColor: colorBgMap[highlight.color] || colorBgMap.blue,
                            }}
                          />
                        )}
                      </AnimatePresence>
                      {selected && (
                        <motion.div
                          className="absolute inset-0 border-2 border-blue-500 pointer-events-none rounded-sm"
                          layoutId="cell-selection"
                          transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                        />
                      )}
                      <span className="relative z-10">{value}</span>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
